import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MentalHelper} from "../../utils/MentalHelper";
import {Mental} from "../../Mental";
import {TimelineLite, TweenLite} from "gsap";
import {IMentalGameData} from "../../server/MentalData";
import {MentalNormalSymbol} from "../../reel/symbols/MentalNormalSymbol";
import {FeaturePresentationHelper} from "../../utils/FeaturePresentationHelper";
import {DeadSymbolSticker} from "../../features/DeadSymbolSticker";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MentalGameMode} from "../../server/MentalGameMode";
import {ReelModelSymbolData} from "../../../framework/reel/ReelModel";
import {NoWinPresentation} from "../../noWin/NoWinPresentation";
import {SoundManager} from "../../sound/SoundManager";
import {MentalSoundConfig} from "../../MentalSoundConfig";

export interface IDeadTransformAnim {
    reelId: number,
    symId: number
    size: number,
    segment: number,
    absoluteIdx?: number,
    revealTime?: boolean
}

export class DeadTransform extends PIXI.Container {
    public static isMultCollectSoundPlayed: boolean = false;
    public _flashAnim: TimelineSprite;
    private _blastAnim: TimelineSprite;
    private _reelId: number;
    private _symId: number;
    private _symSize: number;
    private _symSegment: number;
    private _shineContainer: PIXI.Container;
    private _gameData: IMentalGameData;
    private _revealTime: boolean = false;
    private _absoluteIdx: number = -1;

    private _prevXShineContainer: number = -1;
    private _prevYShineContainer: number = -1;

    constructor(params: IDeadTransformAnim) {
        super();

        this.setParams(params);
    }

    private static createPNGSeq(assetName: string): TimelineSprite {
        let pngSeq: TimelineSprite = MentalHelper.createSprite(assetName);
        pngSeq.blendMode = PIXI.BLEND_MODES.ADD;

        return pngSeq;
    }

    private static createFlashAnim(): TimelineSprite {
        let anim: TimelineSprite = DeadTransform.createPNGSeq("flashShine");
        anim.position.set(0, 100);
        anim.scale.set(2, 1.3);
        return anim;
    }

    private static getBlastAnimation(scalingFactor: number[], animPos: number[]): TimelineSprite {
        let anim: TimelineSprite = DeadTransform.createPNGSeq("deadBlast");
        anim.anchor.set(0, 0.5);
        anim.scale.set(scalingFactor[0], scalingFactor[1]);
        anim.position.set(animPos[0], animPos[1]);

        return anim;
    }

    public playAnimSeq(absolutePosition: number, symSegment: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            isWinSym: boolean = NoWinPresentation.getWinNoWinSymStatus(this._reelId, this._symId, absolutePosition);

        tl.add(() => Mental.deadFeaturePresentation.removeAllVisibleDeadToActiveAnimations(), 0);

        if ((Mental.specialDeadMultiplierReveal > 999 && isWinSym && Mental.model.gameData.deadSymbols[this._reelId][this._symId][symSegment].multiplier === Mental.specialDeadMultiplierReveal) || (Mental.specialDeadMultiplierReveal < 1000 && isWinSym)) {
            tl.add(this._flashAnim.getAnimationAutoShowHide(true, true));

            tl.add(this._blastAnim.getAnimationAutoShowHide(true, true));
        }

        if (Mental.model.gameData.nextMode !== MentalGameMode.NORMAL) {
            Mental.deadFeatureStickyMultiplier.showIconStickers(this._gameData.stickyDeadSymCounterNextSpin);
            tl.add(() => {
                this.playFlyAnim()
                    .add(() => {
                        Mental.deadFeatureStickyMultiplier.playStickyAnim(this._gameData.stickyDeadSymCounterNextSpin);
                        this.showMultiplier();
                    })
            }, isWinSym ? (tl.totalDuration() - 0.5) : tl.totalDuration());
        }

        return tl;
    }

    public playFlyAnim(): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {this._shineContainer.visible = false}
        });

        if (this._gameData.mode !== MentalGameMode.NORMAL && this._gameData.mode !== MentalGameMode.STANDARD_FREESPIN) {
            const symName: string = ((this._gameData.finalEvaluatedArea[this._reelId][this._symId][this._symSegment])).name;
            const symStickers: DeadSymbolSticker[] = Mental.deadFeatureStickyMultiplier._stickySymbolsOnReel;

            for (let i = 0; i < symStickers.length; i++) {
                const currSticker: DeadSymbolSticker = symStickers[i];
                if (currSticker.name === symName) {
                    const marginX: number = NolimitApplication.isLandscape ? 30 : 60,
                        marginY: number = NolimitApplication.isLandscape ? 0 : -20,
                        posX: number = MentalHelper.copyLocalPosition(this._shineContainer, currSticker).x - 50 - 70 - marginX,
                        posY: number = MentalHelper.copyLocalPosition(this._shineContainer, currSticker).y - 90 - marginY;

                    // #377
                    tl.add(() => {this._shineContainer.visible = true}, 0);

                    tl.add(TweenLite.to(this._shineContainer, {alpha: 1, duration: 0.05}));
                    tl.add(TweenLite.to(this._shineContainer.position, {
                        x: posX,
                        y: posY,
                        duration: 0.3,
                        onStart: () => {
                            if (!(DeadTransform.isMultCollectSoundPlayed)) {
                                DeadTransform.isMultCollectSoundPlayed = true;
                                SoundManager.playSoundEffect(MentalSoundConfig.DEAD_COLLECT);
                            }
                        },
                        onComplete: () => {
                            TweenLite.to(this._shineContainer, {alpha: 0, duration: 0.3});
                        }
                    }));
                }
            }

        }
        return tl;
    }

    public setData(reelId: number, symId: number, symSegment: number): void {
        if (Mental.deadFeaturePresentation.isCoolOrSuperFs(this._gameData.mode)) {
            const params: IDeadTransformAnim = {
                "reelId": reelId,
                "symId": symId,
                "size": Mental.model.gameData.currentSymbolSize[reelId][symId],
                "segment": symSegment
            }

            this.setParams(params);

            this._prevXShineContainer = this._shineContainer.position.x;
            this._prevYShineContainer = this._shineContainer.position.y;
        }
    }

    public playFlyBackAnim(symName: string, multValue: number, reelId: number, symId: number, symSegment: number, onReelStop: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            paused: true,
            onComplete: () => {
                // #377
                this._shineContainer.visible = false;
                this._blastAnim.hide();
            }
        });

        if (Mental.deadFeaturePresentation.isCoolOrSuperFs(this._gameData.mode)) {
            const symStickers: DeadSymbolSticker[] = Mental.deadFeatureStickyMultiplier._stickySymbolsOnReel;

            tl.add(() => {
                if ((reelId === 1 || reelId === 3) && symId === 0) { // to fix #312,#315
                    if (FeaturePresentationHelper._symbolHeightOnReel[reelId] !== -1) {
                        this.position.y = (FeaturePresentationHelper._symbolHeightOnReel[reelId] * (symSegment)) + 99;
                        this._prevYShineContainer = this._shineContainer.position.y = this.position.y + (FeaturePresentationHelper._symbolHeightOnReel[reelId]) - 20;
                    }
                }

                // #328
                if (symId === 2 && ((reelId === 1 && this._gameData.xWaysPlayed[2]) || (reelId === 3 && this._gameData.xWaysPlayed[3]))) {
                    const topSymbolCount: number = this._gameData.currentSymbolSize[reelId][0] + this._gameData.currentSymbolSize[reelId][1];
                    this.position.y = (FeaturePresentationHelper._symbolHeightOnReel[reelId] * (topSymbolCount + symSegment)) + 99;
                    this._prevYShineContainer = this._shineContainer.position.y = this.position.y + (FeaturePresentationHelper._symbolHeightOnReel[reelId]) - 20;
                }
            }, 0);

            for (let i: number = 0; i < symStickers.length; i++) {
                const currSticker: DeadSymbolSticker = symStickers[i];
                if (currSticker.name === symName) {
                    let symbolData: ReelModelSymbolData;
                    let currSymbol: MentalNormalSymbol;

                    tl.add(() => {
                        if (symId === 2 && ((reelId === 1 && !this._gameData.xWaysPlayed[2]) || (reelId === 3 && !this._gameData.xWaysPlayed[3]))) { // check corrected to fix #274
                            this._prevYShineContainer -= 99;
                            this._blastAnim.position.y = 178 - 99;
                            if (symSegment === 1) {
                                this._prevYShineContainer -= 198;
                                this._blastAnim.position.y -= 198;
                            }
                        }
                    }, 0);

                    tl.add(() => {
                        // getting S2 when in case of S2 transform,when not accessing symbol within tl
                        if (onReelStop) { // to fix #336
                            symbolData = Mental.reelArea.getReel(reelId).model.getStopSymbolAt(symId);
                        } else {
                            symbolData = Mental.reelArea.getReel(reelId).model.getReelSymbolAt(symId);
                        }

                        currSymbol = <MentalNormalSymbol>symbolData.positionable;
                    }, 0);
                    tl.add(() => {
                        if (currSymbol._splitSymbols.length > 1) {
                            currSymbol._splitSymbols[symSegment]._symbolMultiplierValue = multValue;
                            currSymbol._splitSymbols[symSegment]._symbolMultiplier.visible = false; // #167 pt-8
                            currSymbol._splitSymbols[symSegment].addMultiplier();
                        } else {
                            currSymbol._symbolMultiplierValue = multValue;
                            currSymbol._symbolMultiplier.visible = false; // #167 pt-8
                            currSymbol.addMultiplier();
                        }
                        //hide stickyIcon in case of cool FS
                        if (this._gameData.mode === MentalGameMode.COOL_FREESPIN) {
                            const count = currSticker._count - 1;
                            if (count == 0) {
                                currSticker.updateMultiplierTo(0);
                                currSticker.updateSymCount(0);
                                currSticker.dimDown();
                            } else {
                                currSticker.updateSymCount(count);
                            }
                        }
                    }, 0);

                    // #377
                    tl.add(() => {this._shineContainer.visible = true}, 0);

                    tl.add(() => {TweenLite.to(this._shineContainer, {alpha: 1, duration: 0.05})}, 0);
                    tl.add(() => {
                        TweenLite.fromTo(this._shineContainer.position, {
                            x: MentalHelper.copyLocalPosition(this._shineContainer, currSticker).x - 50 - 70 - (NolimitApplication.isLandscape ? 30 : 60),
                            y: MentalHelper.copyLocalPosition(this._shineContainer, currSticker).y - 90 - (NolimitApplication.isLandscape ? 0 : -20)
                        }, {
                            x: this._prevXShineContainer,
                            y: this._prevYShineContainer,
                            duration: 0.25,
                            onStart: () => {
                                SoundManager.stopSoundEffect(MentalSoundConfig.DEAD_DISTRIBUTE);
                                SoundManager.playSoundEffect(MentalSoundConfig.DEAD_DISTRIBUTE);
                            }
                        })
                    }, 0);
                    tl.add(() => {
                        TweenLite.to(this._shineContainer, {
                            alpha: 0, duration: 0.05,
                            onComplete: () => {
                                SoundManager.stopSoundEffect(MentalSoundConfig.DEAD_DISTRIBUTE_UPDATE);
                                SoundManager.playSoundEffect(MentalSoundConfig.DEAD_DISTRIBUTE_UPDATE);
                            }
                        })
                    }, 0.25);

                    tl.add(() => {
                        if (currSymbol._splitSymbols.length > 1) {
                            currSymbol._splitSymbols[symSegment].showSymbolMultiplier();
                        } else {
                            currSymbol.showSymbolMultiplier();
                        }
                    }, 0.3);

                    tl.add(() => {
                        if ((reelId === 1 && this._gameData.xWaysPlayed[0]) || (reelId === 3 && this._gameData.xWaysPlayed[1])) {
                            // to fix #318
                            const isEnhancer: boolean = ((reelId === 1 && this._gameData.xWaysPlayed[2]) || (reelId === 3 && this._gameData.xWaysPlayed[3]));
                            FeaturePresentationHelper.resizeSymbolWithInReel(reelId, isEnhancer);
                        }

                        // to fix #328
                        if (symId === 2 && (reelId === 1 && this._gameData.xWaysPlayed[2]) || (reelId === 3 && this._gameData.xWaysPlayed[3])) {
                            FeaturePresentationHelper.resizeSymbolWithInReel(reelId, true);
                        }
                    }, 0.3);
                    tl.add(() => {this._blastAnim.getAnimationAutoShowHide(true, true)}, 0.3);
                    tl.add(() => {}, 0.3 + this._blastAnim.totalDuration);
                }
            }

            if (Mental.deadFeaturePresentation.isOnEnhancer(reelId, symId)) {
                Mental.xWaysCounter.updateXwaysCounterFromMidSym(reelId, multValue - 1); // 1 added through updateXwaysCounterFromEnhancerOpen
            } else if (Mental.deadFeaturePresentation.isS2Transform(reelId, symId)) {
                Mental.xWaysCounter.updateXwaysCounterFromS2Transform(reelId, multValue - 1); // already counted as 1 symbol
            }
        }

        Mental.deadFeatureStickyMultiplier.alignStickers();
        return tl.play(0);
    }

    private showMultiplier(): TimelineLite {
        let symbols: MentalNormalSymbol[] = Mental.reelArea.getReel(this._reelId).getAllVisibleStopSymbolsWithEnhancerSymbols() as MentalNormalSymbol[],
            symWithAllSplits: MentalNormalSymbol[] = FeaturePresentationHelper.getAllSymbolOnReelWithSplits(symbols);
        const tl: TimelineLite = new TimelineLite();

        for (let i: number = 0; i < symWithAllSplits.length; i++) {
            const currSym: MentalNormalSymbol = symWithAllSplits[i];
            if (currSym._symbolMultiplierValue > 1) {
                tl.add(() => currSym.showSymbolMultiplier(), 0);
            }
        }
        return tl;
    }

    private setParams(params: IDeadTransformAnim): void {
        this._reelId = params.reelId;
        this._symId = params.symId;
        this._symSize = params.size;
        this._symSegment = params.segment;
        this._gameData = Mental.model.gameData;

        if (params.revealTime) {
            this._revealTime = params.revealTime;
        }
        if (params.absoluteIdx !== undefined) {
            this._absoluteIdx = params.absoluteIdx;
        }

        this.init();
    }

    private init(): void {
        let onFeatureReel: boolean = (this._reelId === 1 || this._reelId === 3);

        if (this._revealTime && this._symId === 2 && onFeatureReel) { // for enhancer only
            this._symSize *= 2;
        }

        let defSymHeight: number = 198,
            finalSymHeight: number = (defSymHeight / this._symSize);

        if (this._revealTime && this._symId !== 2 && FeaturePresentationHelper._symbolHeightOnReel[this._reelId] !== -1) {
            finalSymHeight = FeaturePresentationHelper._symbolHeightOnReel[this._reelId];
        }

        let mainScale: number[] = [1, (finalSymHeight / defSymHeight)],
            childScale: number[] = [2, ((defSymHeight / finalSymHeight) * 2)],
            multAnimPos: number[] = [-40, (defSymHeight - 20)],
            animPosX: number = Mental.reelArea.getReel(this._reelId).position.x,
            animPosY: number;

        this._flashAnim = DeadTransform.createFlashAnim();
        this._flashAnim.hide();

        this._blastAnim = DeadTransform.getBlastAnimation(childScale, multAnimPos);
        this._blastAnim.hide();

        let positionY: number = 0;

        if (this._symId !== 2 && this._revealTime && onFeatureReel && FeaturePresentationHelper._symbolHeightOnReel[this._reelId] !== -1) {
            if (this._symId === 0) {
                positionY = 99 + this._absoluteIdx * FeaturePresentationHelper._symbolHeightOnReel[this._reelId];
            } else if (this._symId === 1) {
                positionY = 99 + (this._gameData.finalSymbolSize[this._reelId][0] * FeaturePresentationHelper._symbolHeightOnReel[this._reelId]);
                positionY += this._absoluteIdx * FeaturePresentationHelper._symbolHeightOnReel[this._reelId];
            }
        } else {
            if (this._absoluteIdx !== -1) {
                positionY = ((defSymHeight * this._symId) + ((defSymHeight / this._symSize) * this._absoluteIdx)) + 99;
            } else {
                positionY = ((defSymHeight * this._symId) + ((defSymHeight / this._symSize) * this._symSegment)) + 99;
            }
        }

        animPosY = onFeatureReel ? positionY : ((defSymHeight * this._symId) + ((defSymHeight / this._symSize) * this._symSegment));
        this.scale.set(mainScale[0], mainScale[1]);
        this.position.set(animPosX, animPosY);

        this.addChild(this._flashAnim, this._blastAnim);

        this._shineContainer = this.createFlyByShine(animPosX, animPosY, finalSymHeight);
        Mental.deadFeaturePresentation.addChild(this._shineContainer);
    }

    private createFlyByShine(posX: number, posY: number, symHeight: number): PIXI.Container {
        const shineImg: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("flyShine")[0]),
            shineContainer: PIXI.Container = new PIXI.Container();

        shineContainer.name = "shineContainer_" + this._reelId + "_" + this._symId + "_" + this._symSegment;

        shineImg.anchor.set(0, 0.5);
        shineImg.scale.set(2);
        shineImg.blendMode = PIXI.BLEND_MODES.ADD;

        shineContainer.position.set(posX - 50, posY + symHeight - 20);
        shineContainer.alpha = 0;
        shineContainer.visible = false;
        shineContainer.addChild(shineImg);

        return shineContainer;
    }
}